/**
 * SilenceManager.tsx — Alert silence management panel
 *
 * Purpose:    Allows operators to create timed alert silences (by matcher pattern)
 *             and delete existing ones. Renders a form + active silences list.
 * Inputs:     silences: Silence[], onCreate, onDelete callbacks
 * Outputs:    Form for new silence + list of active silences with delete buttons
 * Constraints: Duration is in hours (integer); matcher is a raw string key=value
 */
import { useState } from 'react'
import type { Silence } from '../../hooks/useAlertRouterData'

export function SilenceManager({
  silences,
  onCreate,
  onDelete,
}: {
  silences: Silence[]
  onCreate: (s: Omit<Silence, 'id' | 'createdBy'>) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
}) {
  const [matcher, setMatcher] = useState('')
  const [reason, setReason] = useState('')
  const [hours, setHours] = useState('2')
  const [saving, setSaving] = useState(false)

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!matcher.trim()) return
    setSaving(true)
    const startsAt = new Date().toISOString()
    const endsAt = new Date(Date.now() + Number(hours) * 3_600_000).toISOString()
    await onCreate({ matcher, reason, startsAt, endsAt })
    setSaving(false)
    setMatcher('')
    setReason('')
  }

  return (
    <div>
      <form onSubmit={(e) => { void handleCreate(e) }} className="mb-4 flex flex-wrap gap-2">
        <input
          type="text"
          value={matcher}
          onChange={(e) => setMatcher(e.target.value)}
          placeholder='Matcher (e.g. source="uptime-monitor")'
          className="flex-1 rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
        />
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason"
          className="w-40 rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
        />
        <input
          type="number"
          value={hours}
          min={1}
          onChange={(e) => setHours(e.target.value)}
          className="w-20 rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink focus:border-sky-500 focus:outline-none"
          title="Duration (hours)"
        />
        <span className="self-center text-sm text-brand-muted">hrs</span>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-orange-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
        >
          {saving ? 'Silencing…' : 'Silence'}
        </button>
      </form>

      {silences.length === 0 ? (
        <p className="text-sm text-brand-muted">No active silences.</p>
      ) : (
        <ul className="space-y-2">
          {silences.map((s) => (
            <li key={s.id} className="flex items-center justify-between rounded-md border border-brand-subtle px-3 py-2 text-sm">
              <div>
                <span className="font-mono text-xs text-yellow-700 dark:text-yellow-300">{s.matcher}</span>
                {s.reason && <span className="ml-2 text-brand-muted">{s.reason}</span>}
                <span className="ml-2 text-xs text-brand-muted">
                  until {new Date(s.endsAt).toLocaleString()}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { void onDelete(s.id) }}
                className="ml-4 text-xs text-red-700 dark:text-red-400 hover:underline"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
